/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

import { getCookie, signOut } from "@/entities/auth/api";
import { URL_PATHS } from "@/shared/constants/url-path";
import AlertModal from "@/shared/ui/modal/alert-modal";

type NetworkErrorReason = "entry" | "runtime";
type NetworkErrorHandler = (reason?: NetworkErrorReason) => void;

export const NetworkErrorContext = createContext<NetworkErrorHandler | null>(
  null
);

const NETWORK_ERROR_MESSAGES = new Set(["Failed to fetch", "Load failed"]);

export const isNavigatorOffline = () =>
  typeof navigator !== "undefined" && !navigator.onLine;

const hasSession = () =>
  Boolean(getCookie("accessToken") || getCookie("refreshToken"));

export default function NetworkErrorProvider({
  children,
}: NetworkErrorProviderProps) {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(() => isNavigatorOffline());
  const [networkErrorReason, setNetworkErrorReason] =
    useState<NetworkErrorReason>(() =>
      isNavigatorOffline() ? "entry" : "runtime"
    );
  const isHandlingRef = useRef(false);

  const handleNetworkError = useCallback(
    (reason: NetworkErrorReason = "runtime") => {
      setNetworkErrorReason(reason);
      setIsModalOpen(true);

      if (isHandlingRef.current) {
        return;
      }

      isHandlingRef.current = true;

      // 오프라인 상태 감지 즉시 세션 종료 및 루트 페이지로 리다이렉트 처리
      void (async () => {
        if (hasSession()) {
          await signOut();
        }

        navigate(URL_PATHS.ROOT, { replace: true });
      })();
    },
    [navigate]
  );

  // 앱 진입 시 이미 오프라인인 경우 처리
  useLayoutEffect(() => {
    if (isNavigatorOffline()) {
      handleNetworkError("entry");
    }
  }, [handleNetworkError]);

  useEffect(() => {
    // 앱 사용 중 offline 이벤트가 발생한 경우 처리
    const handleOfflineEvent = () => {
      handleNetworkError("runtime");
    };

    const handleOnlineEvent = () => {
      isHandlingRef.current = false;
      setIsModalOpen(false);
      setNetworkErrorReason("runtime");
    };

    window.addEventListener("offline", handleOfflineEvent);
    window.addEventListener("online", handleOnlineEvent);

    return () => {
      window.removeEventListener("offline", handleOfflineEvent);
      window.removeEventListener("online", handleOnlineEvent);
    };
  }, [handleNetworkError]);

  const handleRetry = () => {
    window.location.reload();
  };

  return (
    <NetworkErrorContext.Provider value={handleNetworkError}>
      {children}
      <AlertModal
        isOpen={isModalOpen}
        onConfirm={handleRetry}
        title="네트워크 오류"
        confirmText={
          networkErrorReason === "entry" ? "다시 로그인하기" : "확인"
        }
        closeOnOverlayClick={false}
      >
        {networkErrorReason === "entry" ? (
          <>
            현재 인터넷에 연결되어 있지 않아, <br />
            원바원에 접속할 수 없습니다. <br />
            <span className="pt-2">
              인터넷 연결 상태 확인 후 다시 시도해주세요.
            </span>
          </>
        ) : (
          <>
            원바원 서버에 연결할 수 없어, <br />
            자동 로그아웃되었습니다. <br />
            <span className="pt-2">
              인터넷 연결 상태 확인 후 다시 시도해주세요.
            </span>
          </>
        )}
      </AlertModal>
    </NetworkErrorContext.Provider>
  );
}

interface NetworkErrorProviderProps {
  children: ReactNode;
}

export function useNetworkErrorHandler() {
  const context = useContext(NetworkErrorContext);

  if (!context) {
    throw new Error(
      "useNetworkErrorHandler must be used within NetworkErrorProvider"
    );
  }

  return context;
}

export function isNetworkError(error: unknown): boolean {
  return (
    isNavigatorOffline() ||
    (error instanceof Error && NETWORK_ERROR_MESSAGES.has(error.message))
  );
}

export function useNetworkErrorFallback(
  error: unknown,
  resetError?: () => void
) {
  const handleNetworkError = useNetworkErrorHandler();
  const networkError = isNetworkError(error);

  useEffect(() => {
    if (!networkError) {
      return;
    }

    resetError?.();
    handleNetworkError();
  }, [handleNetworkError, networkError, resetError]);

  return networkError;
}
